import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, EnvelopeSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ProjectModal } from "@/components/Projects/ProjectModal";
import { useAuth } from "@/context/AuthContext";
import { PROJECT_STATUSES, DEPARTMENTS } from "@/constants";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { toast } from "sonner";

const VIEW_LABELS = {
  all: "All Projects", mine: "My Projects", overdue: "Overdue",
  completed: "Completed", onhold: "On Hold", archived: "Archived",
};

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ status: "", department: "", search: "" });
  const [view, setView] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    const params = { archived: view === "archived" };
    if (view === "onhold") params.status = "On Hold";
    else if (filters.status) params.status = filters.status;
    if (filters.department) params.department = filters.department;
    if (filters.search) params.search = filters.search;
    const { data } = await api.get("/projects", { params });
    let list = data;
    if (view === "mine") list = list.filter((p) => p.owner_id === user?.id || p.created_by === user?.id);
    else if (view === "overdue") list = list.filter((p) => p.overdue_count > 0);
    else if (view === "completed") list = list.filter((p) => p.completion_pct >= 100);
    setProjects(list);
  };

  useEffect(() => { load(); }, [filters, view]);

  const canCreate = user?.role === "admin" || user?.role === "pm";
  const [emailingId, setEmailingId] = useState(null);

  const handleSendEmail = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    setEmailingId(projectId);
    try {
      const { data } = await api.post(`/projects/${projectId}/send-assignment-email`);
      if (data.total_recipients === 0) toast.info("No one is assigned to any task on this project yet");
      else if (data.sent.length === 0) toast.warning("In-app notifications sent, but emails failed — check Microsoft Graph is configured");
      else toast.success(`Notified ${data.sent.length} of ${data.total_recipients} assignee${data.total_recipients > 1 ? "s" : ""} (email + in-app)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send notifications");
    } finally {
      setEmailingId(null);
    }
  };

  return (
    <div data-testid="projects-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-caps">Product Lines</span>
          <h1 className="text-2xl font-display font-extrabold mt-1">{view === "all" ? "Projects" : `${VIEW_LABELS[view]}`}</h1>
        </div>
        {canCreate && (
          <button data-testid="new-project-btn" onClick={() => setModalOpen(true)} className="btn-navy flex items-center gap-2">
            <Plus size={14} /> New Project
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={view} onValueChange={setView}>
          <SelectTrigger data-testid="project-view-select" className="w-44 font-semibold" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(VIEW_LABELS).map(([k, label]) => (
              <SelectItem key={k} value={k} data-testid={`project-view-${k}`}>{k === "archived" ? label : label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input data-testid="project-search-input" placeholder="Search projects..." value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="max-w-xs" style={{ borderRadius: "2px" }} />
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="project-status-filter" className="w-44" style={{ borderRadius: "2px" }}><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.department} onValueChange={(v) => setFilters({ ...filters, department: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="project-department-filter" className="w-44" style={{ borderRadius: "2px" }}><SelectValue placeholder="All Departments" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Departments</SelectItem>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
        {projects.length === 0 && <div className="text-sm font-mono text-muted-foreground col-span-full">&gt; no projects match this view</div>}
        {projects.map((p) => (
          <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`} className="card-flat p-4 hover:shadow-md transition-shadow block">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-display font-bold text-sm">{p.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.product_line}</div>
              </div>
              <div className="flex items-center gap-1.5">
                {p.archived && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-muted text-muted-foreground" style={{ borderRadius: "2px" }} data-testid={`project-archived-badge-${p.id}`}>Archived</span>}
                <StatusBadge status={p.status} />
                {p.is_draft && <span className="text-[10px] font-mono font-bold uppercase px-1.5 py-0.5 bg-amber-100 text-amber-700" style={{ borderRadius: "2px" }}>Draft</span>}
              </div>
            </div>
            <div className="w-full h-1.5 bg-muted mt-3" style={{ borderRadius: "2px" }}>
              <div className="h-1.5 bg-accent" style={{ width: `${p.completion_pct}%`, borderRadius: "2px" }} />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
              <span>{p.completion_pct}% complete</span>
              {p.overdue_count > 0 && <span className="text-destructive font-semibold">{p.overdue_count} overdue</span>}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-xs">
              <span>Owner: <span className="font-medium">{p.owner_name || "—"}</span></span>
              <span className="font-mono">{p.target_production_date ? format(new Date(p.target_production_date), "d MMM yyyy") : "—"}</span>
            </div>
            {(user?.role === "admin" || (p.created_by || p.owner_id) === user?.id) && (
              <button data-testid={`send-assignment-email-btn-${p.id}`} onClick={(e) => handleSendEmail(e, p.id)} disabled={emailingId === p.id}
                className="w-full mt-3 flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold uppercase border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
                style={{ borderRadius: "2px" }}>
                <EnvelopeSimple size={13} />{emailingId === p.id ? "Sending..." : "Send Notifications"}
              </button>
            )}
          </Link>
        ))}
      </div>

      {modalOpen && <ProjectModal onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />}
    </div>
  );
}
